<template>
  <div
    v-if="event"
    class="po-card"
    :class="`kind-${event.kind || 'default'}`"
  >
    <div class="po-head">
      <div>
        <div class="po-kicker">
          Nick adapte la suite
        </div>
        <h3 class="po-title">
          {{ kindLabel(event.kind) }}
        </h3>
      </div>
      <span class="po-pill">{{ kindShort(event.kind) }}</span>
    </div>

    <p
      v-if="mainMessage"
      class="po-message"
    >
      {{ mainMessage }}
    </p>

    <div class="po-meta">
      <span v-if="event.conceptLabel"><strong>Concept :</strong> {{ event.conceptLabel }}</span>
      <span v-if="event.targetLessonTitle"><strong>Suite :</strong> {{ event.targetLessonTitle }}</span>
      <span v-if="event.dueAfterCoreCompletions"><strong>Rappel :</strong> après {{ event.dueAfterCoreCompletions }} leçons validées</span>
    </div>

    <div
      v-if="recentHistory.length > 1"
      class="po-history"
    >
      <div class="po-history-title">
        Historique récent
      </div>
      <div
        v-for="(entry, index) in recentHistory.slice(1)"
        :key="`path-history-${index}`"
        class="po-history-row"
      >
        <span class="po-history-kind">{{ kindShort(entry.kind) }}</span>
        <span class="po-history-text">{{ entryText(entry) }}</span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PathAdaptationCard',
  props: {
    event: {
      type: Object,
      default: () => null,
    },
    history: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    recentHistory() {
      return Array.isArray(this.history) ? this.history.slice(0, 4) : []
    },
    mainMessage() {
      return this.entryText(this.event)
    },
  },
  methods: {
    kindLabel(kind) {
      return {
        accelerate: 'Accélération visible',
        practice_boost: 'Pratique ciblée',
        remediation: 'Renfort',
        reexplain: 'Renfort',
        challenge: 'Mini challenge',
        spaced_review: 'Révision reprogrammée',
        review_scheduled: 'Révision planifiée',
      }[kind] || 'Adaptation du parcours'
    },
    kindShort(kind) {
      return {
        accelerate: 'Accéléré',
        practice_boost: 'Ajouté',
        remediation: 'Renfort',
        reexplain: 'Renfort',
        challenge: 'Challenge',
        spaced_review: 'Révision',
        review_scheduled: 'Planifié',
      }[kind] || 'Adapté'
    },
    entryText(entry) {
      const raw = (entry && (entry.message || entry.rationale)) || ''
      return String(raw)
        .replace(/\*\*/g, '')
        .replace(/\n+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
    },
  },
}
</script>

<style scoped>
.po-card {
  background: #fff;
  border: 1px solid #e9d5ff;
  border-radius: 16px;
  padding: 18px 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  box-shadow: 0 4px 18px rgba(60, 40, 150, 0.05);
}
.po-card.kind-accelerate {
  background: #f0fdf4;
  border-color: #bbf7d0;
}
.po-card.kind-practice_boost {
  background: #eff6ff;
  border-color: #bfdbfe;
}
.po-card.kind-review_scheduled,
.po-card.kind-spaced_review {
  background: #ecfeff;
  border-color: #a5f3fc;
}
.po-card.kind-reexplain {
  background: #fff7ed;
  border-color: #fed7aa;
}
.po-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}
.po-kicker {
  color: #7a6ed8;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}
.po-title {
  margin: 4px 0 0;
  color: #26254a;
  font-size: 18px;
  font-weight: 700;
}
.po-pill {
  display: inline-flex;
  align-items: center;
  padding: 5px 10px;
  border-radius: 999px;
  background: rgba(124, 58, 237, 0.12);
  color: #6d28d9;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}
.po-card.kind-accelerate .po-pill {
  background: rgba(22, 163, 74, 0.12);
  color: #15803d;
}
.po-card.kind-practice_boost .po-pill {
  background: rgba(37, 99, 235, 0.12);
  color: #1d4ed8;
}
.po-card.kind-review_scheduled .po-pill,
.po-card.kind-spaced_review .po-pill {
  background: rgba(8, 145, 178, 0.12);
  color: #0e7490;
}
.po-card.kind-reexplain .po-pill {
  background: rgba(234, 88, 12, 0.12);
  color: #c2410c;
}
.po-message {
  margin: 0;
  color: #334155;
  font-size: 0.95rem;
  line-height: 1.6;
}
.po-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 10px 18px;
  color: #5b5a7c;
  font-size: 0.84rem;
}
.po-history {
  border-top: 1px solid rgba(124, 58, 237, 0.12);
  padding-top: 12px;
}
.po-history-title {
  color: #5b5a7c;
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 8px;
}
.po-history-row {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  font-size: 0.86rem;
  color: #334155;
}
.po-history-row + .po-history-row {
  margin-top: 6px;
}
.po-history-kind {
  min-width: 68px;
  color: #6d28d9;
  font-weight: 700;
}
.po-history-text {
  flex: 1;
}
@media (max-width: 900px) {
  .po-head {
    flex-direction: column;
    align-items: flex-start;
  }
  .po-meta {
    flex-direction: column;
    gap: 6px;
  }
}
</style>
